/* eslint-disable react/no-danger */
import { Link } from '@pmi/dsm-react';
import { ReactNode, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Product, ProductBundleOption } from '../../models';
import {
  useGetLocalChaptersQuery,
  usePlaceOrderMutation,
  useRedemptionEnquiryMutation,
  OrderPayload,
} from './apiSlice';
import { toastWithAnalytics } from '../../../../shared/analytics';
import ConfirmationModal from './ConfirmationModal';
import ProfileUpdateModal from './ProfileUpdateModal';
import { adminInfoSelector } from '../../../../shared/store/shared/adminInfoSlice';
import EnhancedEnrollmentModal from './EnhancedEnrollmentModal';
import { useGetAccountDataQuery } from '../../../../shared/navigation/apiSlice';
import { useAddToCartMutation } from '../apiSlice';
import { openCart } from '../../../../shared/store/shared/cartSlice';
import Overlay from '../../../../shared/components/Overlay';

interface ProductModalProps {
  accountId: string;
  product: Product;
  onCancel: (report?: boolean) => void;
  onSuccess: () => void;
  onSubmit: () => void;
}

export default function ProductModal({
  accountId,
  product,
  onCancel,
  onSubmit,
  onSuccess,
}: ProductModalProps) {
  const [
    placeOrder,
    { isLoading: placeOrderLoading, isError: placeOrderError, reset },
  ] = usePlaceOrderMutation();
  const [addToCart, { isLoading: addToCartLoading, isError: addToCartError }] =
    useAddToCartMutation();
  const [apiError, setApiError] = useState(false);
  const [geoLocationLoading, setGeoLocationLoading] = useState(false);
  const [geoLocation, setGeoLocation] = useState({
    latitude: null,
    longitude: null,
    distance: null,
  });
  const [showModal, setShowModal] = useState(true);
  const { adminDetails } = useSelector(adminInfoSelector);
  const bundleContainsCert = product.bundleOptions.some((x) =>
    x.productLinks.some((y) => y.fulfillmentProvider === 'Certification'),
  );
  const { data: accountData, isLoading } = useGetAccountDataQuery(accountId);
  const [
    triggerRedemptionEnquiry,
    { data: profileData, isLoading: profileLoading, isError: profileError },
  ] = useRedemptionEnquiryMutation();

  const dispatch = useDispatch();

  const GetGeoLocation = () => {
    if (profileData && profileData.requestAddressInfo) {
      setGeoLocationLoading(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setGeoLocation({ latitude, longitude, distance: 100 });
          setGeoLocationLoading(false);
        },
        () => setGeoLocationLoading(false),
      );
    } else {
      setGeoLocationLoading(false);
    }
  };

  const IsBundleWithChapter = product.bundleOptions.some((bo) =>
    bo.title?.toLowerCase().includes('chapter'),
  );

  useEffect(() => {
    if (IsBundleWithChapter) {
      GetGeoLocation();
    }
  }, [profileData]);

  const {
    data: chapterList,
    isLoading: chapterListLoading,
    isError: chapterListError,
  } = useGetLocalChaptersQuery(geoLocation, {
    skip:
      profileLoading ||
      !profileData ||
      !IsBundleWithChapter ||
      geoLocationLoading ||
      isLoading ||
      geoLocation.longitude === null,
  });

  useEffect(() => {
    if (!isLoading) {
      triggerRedemptionEnquiry({
        productType: bundleContainsCert ? 'Certification' : product.type,
        accountId,
        dealIds: product.dealIds,
      });
    }
  }, [
    isLoading,
    triggerRedemptionEnquiry,
    bundleContainsCert,
    product.type,
    accountId,
    product.dealIds,
  ]);

  const onProductConfirmation = async (
    bundleOptions?: ProductBundleOption[],
    enrollAutorenew?: boolean,
  ) => {
    const payload: OrderPayload =
      bundleOptions && bundleOptions.length > 0
        ? {
            sku: product.sku,
            bundle_options: bundleOptions,
          }
        : { sku: product.sku };

    if (enrollAutorenew) {
      payload.enroll_autorenew = true;
    }

    // if product is a pay by user product and price > 0, add to cart instead of placing an order
    if (product.isPayByUserProduct && product.price > 0) {
      setApiError(false);
      setShowModal(false);
      await addToCart({
        accountId,
        items: [
          {
            qty: 1,
            sku: product.sku,
            dealCode: product.dealIds[0],
            price: product.price,
            bundleOptions: bundleOptions?.map((option) => ({
              optionId: option.id,
              selectionId: parseInt(option.productLinks[0]?.id, 10),
            })),
          },
        ],
      })
        .unwrap()
        .then(() => {
          dispatch(openCart());
          window.scrollTo({ top: 0, behavior: 'smooth' });
          onSuccess();
          onCancel(false);
        })
        .catch((error) => {
          setApiError(true);
          let apiErrorMessage = 'Something went wrong';

          try {
            const errorValue = JSON.parse(error?.data?.Value);

            // default error message
            apiErrorMessage = errorValue?.error || apiErrorMessage;

            // try parsing detail if it's valid JSON
            try {
              const detail = JSON.parse(errorValue?.detail);
              if (detail?.message) {
                apiErrorMessage = detail.message;
              }

              if (detail?.parameters) {
                detail.parameters.forEach((v: string, i: number) => {
                  apiErrorMessage = apiErrorMessage.replace(`%${i + 1}`, v);
                });
              }
            } catch {
              // detail is not JSON → just append as plain text
              if (errorValue?.detail) {
                apiErrorMessage += `: ${errorValue.detail}`;
              }
            }
          } catch (e) {
            console.error('Failed to parse error JSON:', e, error?.data?.Value);
          }
          const message = (): ReactNode =>
            apiErrorMessage || (
              <>
                Product could not be added to the cart. Please try again or
                contact your administrator{' '}
                <span data-exclude-lg>
                  {accountData?.accountContactEmail?.length > 0
                    ? `(${accountData?.accountContactEmail})`
                    : adminDetails}
                </span>
              </>
            );
          toastWithAnalytics(message(), 'danger');
          onCancel(false);
        });
      return;
    }

    if (product.callToAction?.url) {
      window.location.href = product.callToAction.url;
    } else {
      setApiError(false);
      setShowModal(false);

      try {
        await placeOrder({
          accountId,
          // TODO: figure out how to deal with multiple deals for
          dealId: product.dealIds[0],
          payload,
        }).unwrap();
        toastWithAnalytics(
          <>
            Your enrollment for{' '}
            <span dangerouslySetInnerHTML={{ __html: product.name }} /> is now
            processing.
          </>,
          'success',
          `Your enrollment for ${product.name} is now processing`,
        );
        window.scrollTo({ top: 0, behavior: 'smooth' });
        onSuccess();
        onCancel(false);
      } catch (error) {
        setApiError(true);
        onCancel();
        const message = (): ReactNode => (
          <>
            Enrollment has failed. Please try again or contact
            <span data-exclude-lg>
              <Link
                href="mailto:PartnerSupport@pmi.org"
                label="PartnerSupport@pmi.org"
                id="eflink"
              />
            </span>
          </>
        );
        toastWithAnalytics(message(), 'danger');
        setTimeout(() => {
          const fl: HTMLAnchorElement = document.querySelector('#eflink');
          if (fl) {
            fl.innerHTML = 'PartnerSupport@pmi.org';
            fl.href = 'mailto:PartnerSupport@pmi.org';
          }
        }, 1);
      }
    }
  };

  useEffect(() => {
    if (profileError) {
      toastWithAnalytics('Error while loading profile', 'danger');
    }
  }, [profileError]);

  const profileLoadingComplete =
    !profileLoading &&
    !profileError &&
    profileData &&
    !geoLocationLoading &&
    !chapterListLoading &&
    !chapterListError;

  return (
    <>
      {profileLoadingComplete && product.bundleProduct && showModal && (
        <EnhancedEnrollmentModal
          accountId={accountId}
          product={product}
          onCancel={onCancel}
          onSubmit={(
            bundleOptions?: ProductBundleOption[],
            enrollAutorenew?: boolean,
          ) => {
            onSubmit();
            onProductConfirmation(bundleOptions, enrollAutorenew);
          }}
          profileData={profileData}
          localChapters={chapterList}
        />
      )}
      {!product.bundleProduct && (
        <>
          {profileLoadingComplete &&
            profileData.requestProfileUpdate &&
            showModal && (
              <ProfileUpdateModal
                product={product}
                onCancel={onCancel}
                onSubmit={onSubmit}
                onSuccess={onProductConfirmation}
                reset={reset}
                isError={placeOrderError}
                phoneInputNeeded={profileData.requestPhoneInfo}
                addressInputNeeded={profileData.requestAddressInfo}
                redemptionProfile={profileData.redemptionProfile}
              />
            )}
          {profileLoadingComplete &&
            !profileData.requestProfileUpdate &&
            showModal && (
              <ConfirmationModal
                product={product}
                onCancel={onCancel}
                onSubmit={() => {
                  onSubmit();
                  onProductConfirmation();
                }}
                reset={reset}
                isError={placeOrderError || apiError}
              />
            )}
        </>
      )}
      <Overlay
        variant="PMIOverlay"
        global
        animated
        fade
        iconColor="brand-dark"
        overlayColor="black"
        overlayOpacity={1}
        show={
          profileLoading ||
          chapterListLoading ||
          geoLocationLoading ||
          (placeOrderLoading && !placeOrderError) ||
          (addToCartLoading && !addToCartError)
        }
      />
    </>
  );
}
